<!--
  ~ Copyright (C) 2026 - Potentially Ltd
  ~
  ~ Please see distribution for license.
  -->

@if (organizationData$ | async; as organizationData) {
  <ptl-v3-auth-layout [organizationData]="organizationData" [showMaintenanceOverlay]="showMaintenanceOverlay">
    @if (!isJiscLogin) {
      <div class="v3-headline">
        @if (!magicLinkSent) {
          @if (!userPreviouslyLoggedIn || !isFormAuthType) {
            <h1 class="v3-title">{{ 'translations.public.title.signIn' | transloco }}</h1>
          }
          @if (!showMagicLinkMode) {
            @if (userPreviouslyLoggedIn && isFormAuthType) {
              <h1 class="v3-title">{{ 'translations.public.title.welcome' | transloco }}</h1>
            }
            <p class="v3-subtitle fs-body-m">
              {{ organizationData.landingPage.welcomeMessage || ('translations.public.description.chooseSignInMethod' | transloco) }}
            </p>
          }
        }
      </div>
    } @else {
      <div class="v3-headline">
        <h1 class="v3-title" [innerHTML]="'translations.public.jisc.title' | transloco"></h1>
        <h2 class="v3-subtitle-large" [innerHTML]="'translations.public.jisc.subtitle' | transloco"></h2>
        <p class="v3-subtitle fs-body-m" [innerHTML]="'translations.public.jisc.description' | transloco"></p>
      </div>
    }

    <div
      class="v3-login-form"
      [formGroup]="authenticationDetails"
      role="form"
      [attr.aria-label]="'translations.global.ariaLabels.signInForm' | transloco"
    >
      @let isLoggedIn = loggedIn$ | async;
      @let isLoginLocked = loginButtonLocked$ | async;
      @let isLoggingIn = loginInProgress$ | async;
      @let isMagicLinkEnabled = magicLinkFeatureFlagEnabled$ | async;

      @if (isSamlAuthType && !isJiscLogin && (!isFormAuthType || organization?.allowFormLoginForExternalUsers)) {
        <ptl-sg-button
          type="stroked"
          color="primary"
          class="v3-sso-button"
          [disabled]="isLoginLocked || isLoggedIn"
          (clicked)="onSsoClick()"
        >
          @if (!isLoggingIn && !isLoggedIn) {
            <span>{{ 'translations.public.button.signInWithSso' | transloco: { organizationName: organization?.name } }}</span>
          } @else {
            <span class="v3-submit-loading">
              <span>{{ 'translations.public.button.loggingIn' | transloco }}</span>
              <mat-spinner [diameter]="18"></mat-spinner>
            </span>
          }
        </ptl-sg-button>
      }

      @if (isSamlAuthType && !isJiscLogin && organization?.allowFormLoginForExternalUsers && !showMagicLinkMode) {
        <div class="v3-divider">
          <span class="overline" [attr.aria-label]="'translations.global.ariaLabels.orSignInWithEmail' | transloco">{{
            'translations.public.label.orSignInWithEmail' | transloco
          }}</span>
        </div>

        @if (organization?.landingPage?.externalLoginMessage) {
          <p class="v3-external-login-message extra-small">{{ organization.landingPage.externalLoginMessage }}</p>
        }
      }

      @if (isFormAuthType || showMagicLinkMode) {
        <ptl-sg-input
          class="v3-field"
          type="email"
          formControlName="email"
          data-testid="login-email"
          [label]="'translations.global.input.label.email' | transloco"
          (keydown.enter)="onSaveClick()"
        ></ptl-sg-input>
      }
      @if (authenticationDetails.controls.email.touched && authenticationDetails.controls.email.hasError('required')) {
        <p class="v3-field-error fs-body-xs-alt">
          {{ 'translations.global.input.error.enterEmail' | transloco }}
        </p>
      }

      @if (isFormAuthType && !showMagicLinkMode) {
        <div class="v3-password-field">
          <ptl-sg-input
            class="v3-field"
            [type]="showPasswordSelected ? 'text' : 'password'"
            formControlName="password"
            data-testid="login-password"
            [label]="'translations.global.input.label.password' | transloco"
            (keydown.enter)="onSaveClick()"
          ></ptl-sg-input>
          <button
            type="button"
            class="v3-password-toggle"
            [attr.aria-label]="'translations.global.ariaLabels.showOrHidePassword' | transloco"
            (click)="showPasswordSelected = !showPasswordSelected"
          >
            <ptl-icon [icon]="showPasswordSelected ? LucideEyeOff : LucideEye" size="xs"></ptl-icon>
          </button>
        </div>
      }
      @if (authenticationDetails.controls.password.touched && authenticationDetails.controls.password.hasError('required')) {
        <p class="v3-field-error fs-body-xs-alt">
          {{ 'translations.global.input.error.enterPassword' | transloco }}
        </p>
      }

      @if (isFormAuthType && !showMagicLinkMode) {
        <div class="v3-forgot-row">
          <button type="button" class="v3-link-action" (click)="onForgotPassword()">
            {{ 'translations.public.button.forgotPassword' | transloco }}
          </button>
        </div>
      }

      @if (showMagicLinkMode && isMagicLinkEnabled) {
        <ptl-v3-magic-link-form
          [email]="authenticationDetails.value.email"
          [magicLinkSent]="magicLinkSent"
          [magicLinkRequestInProgress]="magicLinkRequestInProgress"
          (requestLink)="sendMagicLink()"
          (backToSignIn)="toggleMagicLinkMode()"
        ></ptl-v3-magic-link-form>

        <re-captcha
          #magicLinkCaptchaElem
          [siteKey]="reCaptchaSiteKeyV2"
          (resolved)="onMagicLinkCaptchaResolved($event)"
          size="invisible"
        ></re-captcha>
      }

      @if (isFormAuthType && showTotpInput && !showMagicLinkMode) {
        <p class="v3-field-hint fs-body-xs-alt">
          {{ 'translations.public.description.totpRequiredHint' | transloco: { organizationName: organization?.name } }}
        </p>
        <ptl-sg-input
          class="v3-field"
          type="text"
          formControlName="totp"
          [maxLength]="6"
          placeholder="000000"
          [label]="'translations.account.settings.v3.twoFactor.codeLabel' | transloco"
          [description]="'translations.account.settings.v3.twoFactor.codeDescription' | transloco"
          (keydown.enter)="onSaveClick()"
        ></ptl-sg-input>
      }
      @if (authenticationDetails.controls.totp?.touched && authenticationDetails.controls.totp?.hasError('required')) {
        <p class="v3-field-error fs-body-xs-alt">{{ 'translations.global.input.error.enterTotpCode' | transloco }}</p>
      }

      @if (isJiscLogin) {
        <div class="v3-jisc-search">
          <ptl-sg-input
            class="v3-field"
            [label]="'translations.public.jisc.findOrganization' | transloco"
            [matAutocomplete]="auto"
            (paste)="onTextPaste($event)"
            (keyup)="onSearchInputChange($event)"
          ></ptl-sg-input>

          <mat-autocomplete
            #auto="matAutocomplete"
            [panelWidth]="400"
            class="v3-organization-search-result-autocomplete"
            [displayWith]="displayAutocomplete"
            [thresholdPercent]="0.9"
            (optionsScroll)="onAutocompleteScroll()"
            (optionSelected)="onOrganizationSelected($event)"
          >
            @for (org of organizations; track org.id) {
              <mat-option [value]="org" class="v3-search-result-organization-option">
                @if (org.logo) {
                  <img class="v3-organization-icon" [src]="org.logo" alt="{{ org.name }}" />
                }
                {{ org.name }}
              </mat-option>
            }
            @if (isLoadingMore) {
              <div class="v3-loading-more-indicator">
                <mat-spinner [diameter]="30"></mat-spinner>
              </div>
            }
          </mat-autocomplete>

          @if (loadingOrganizations) {
            <mat-spinner class="v3-jisc-search-spinner" [diameter]="18"></mat-spinner>
          }
        </div>
      }

      <div class="v3-actions">
        @if (isJiscLogin) {
          <ptl-sg-button
            type="flat"
            color="primary"
            class="v3-submit"
            [text]="'translations.public.button.continue' | transloco"
            [disabled]="!jiscSelectedOrganization || isLoginLocked || isLoggedIn"
            (clicked)="onSaveClick()"
          ></ptl-sg-button>
        }

        @if (!isJiscLogin && !showMagicLinkMode && !(isSamlAuthType && !isFormAuthType)) {
          <ptl-sg-button
            type="flat"
            color="primary"
            class="v3-submit"
            data-testid="login-submit"
            [disabled]="isLoginLocked || isLoggedIn"
            (clicked)="onSaveClick()"
          >
            @if (!isLoggingIn && !isLoggedIn) {
              <span>{{ 'translations.public.button.signIn' | transloco }}</span>
            } @else {
              <span class="v3-submit-loading">
                <span>{{ 'translations.public.button.loggingIn' | transloco }}</span>
                <mat-spinner [diameter]="18"></mat-spinner>
              </span>
            }
          </ptl-sg-button>
        }

        @if (allowAlumniLogin) {
          <ptl-sg-button
            type="stroked"
            color="primary"
            class="v3-submit"
            [text]="'translations.public.button.alumniLogin' | transloco"
            (clicked)="openAlumniLogin()"
          ></ptl-sg-button>
        }

        @if (isSamlAuthType && organization?.preArrivals) {
          <ptl-sg-button
            type="stroked"
            color="primary"
            class="v3-submit"
            [text]="'translations.public.button.prearrivalsLogin' | transloco"
            (clicked)="openPrearrivalsLogin()"
          ></ptl-sg-button>
        }

        @if (isMagicLinkEnabled && !showMagicLinkMode) {
          <ptl-sg-button
            type="flat"
            color="primary"
            class="v3-submit v3-submit--soft"
            [text]="'translations.public.button.signInWithEmailLink' | transloco"
            (clicked)="toggleMagicLinkMode()"
          ></ptl-sg-button>
        }

        @if (isFormAuthType && organizationData.allowRegistrations && !showMagicLinkMode) {
          <div class="v3-actions-bottom">
            <button type="button" class="v3-link-action" (click)="onNotMemberClick()">
              {{ 'translations.public.button.signUp' | transloco }}
            </button>
          </div>
        }
      </div>

      @if (isSocialAuthType) {
        <div class="v3-divider">
          <span class="overline" [attr.aria-label]="'translations.global.ariaLabels.orSignUpWith' | transloco">{{
            'translations.public.label.signInWith' | transloco
          }}</span>
        </div>
        <div class="v3-social-buttons">
          <button
            class="v3-social-button v3-social-button--linkedin"
            [title]="'translations.public.label.signInWithLinkedIn' | transloco"
            (click)="signInUsingLinkedIn()"
          >
            <img alt="" src="assets/social/linkedin-white.svg" width="291" height="79" />
          </button>
          <button
            class="v3-social-button v3-social-button--google"
            [title]="'translations.public.label.signInWithGoogle' | transloco"
            (click)="signInUsingGoogle()"
          >
            <img alt="" src="assets/social/google-colored.svg" width="265" height="87" />
          </button>
        </div>
      }
    </div>

    @if (isJiscLogin) {
      <div class="v3-headline">
        <p class="v3-description fs-body-s" [innerHTML]="'translations.public.jisc.buttomDescription' | transloco"></p>
      </div>

      <div class="v3-terms-links">
        <a href="https://digitalcapability.jisc.ac.uk/privacy/">{{ 'translations.public.jisc.privacy' | transloco }}</a>
        <a href="https://digitalcapability.jisc.ac.uk/accessibility-statement/">{{
          'translations.public.jisc.accessibility' | transloco
        }}</a>
      </div>
    }
  </ptl-v3-auth-layout>
} @else {
  <ptl-v3-auth-loading></ptl-v3-auth-loading>
}
